/**
 * invoiceTotals — the four numbers on the money page, and the shape
 * they are counted from.
 *
 * INVOICE-1. Here on their own, with NO imports, for the same reason
 * `timeGrid` is: a spec that can be run with `npx tsx` cannot pull in
 * the Supabase client, which reads `import.meta.env` and is not there
 * outside Vite. Keeping the arithmetic importable on its own is what
 * makes it testable at all.
 *
 *     npx tsx src/services/invoicing.spec.ts
 */

export type InvoiceState = 'draft' | 'sent' | 'part_paid' | 'paid' | 'overdue';

export interface InvoiceRow {
  id: string;
  title: string;
  /** The studio's own number for it, once that column exists. */
  number: string | null;
  clientId: string | null;
  clientName: string | null;
  totalCents: number;
  paidCents: number;
  /** What is still owed. Never negative — an overpayment is not a debt. */
  dueCents: number;
  state: InvoiceState;
  /** When the whole invoice falls due, or null while the column is absent. */
  dueOn: Date | null;
  /** The next instalment still to be taken, if it is on a plan. */
  nextDueAt: Date | null;
  sentAt: Date | null;
  paidAt: Date | null;
  createdAt: Date;
  publicToken: string | null;
  projectId: string | null;
}

/* ─────────────────────── the figures ─────────────────────── */

export interface MoneyTotals {
  outstandingCents: number;
  overdueCents: number;
  dueThisMonthCents: number;
  paidThisMonthCents: number;
  /** How many invoices are behind each figure, for the subtitles. */
  outstandingCount: number;
  overdueCount: number;
}

/**
 * The four numbers at the top.
 *
 * Pure, and separate from the hook, because the arithmetic is the part
 * worth being sure about: "outstanding" is what is still owed on
 * everything not settled, NOT the face value of unpaid invoices. A
 * 4,800 shoot with a 2,400 deposit paid is 2,400 outstanding, and a
 * page that says 4,800 is telling a studio they are owed money they
 * already have.
 */
export function totalsFor(invoices: InvoiceRow[], now = new Date()): MoneyTotals {
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);

  let outstandingCents = 0;
  let overdueCents = 0;
  let dueThisMonthCents = 0;
  let paidThisMonthCents = 0;
  let outstandingCount = 0;
  let overdueCount = 0;

  for (const i of invoices) {
    if (i.state !== 'paid' && i.state !== 'draft') {
      outstandingCents += i.dueCents;
      outstandingCount += 1;
    }
    if (i.state === 'overdue') {
      overdueCents += i.dueCents;
      overdueCount += 1;
    }
    const when = i.dueOn ?? i.nextDueAt;
    if (i.state !== 'paid' && i.state !== 'draft' && when && when >= monthStart && when < monthEnd) {
      dueThisMonthCents += i.dueCents;
    }
    if (i.paidAt && i.paidAt >= monthStart && i.paidAt < monthEnd) {
      paidThisMonthCents += i.paidCents || i.totalCents;
    }
  }

  return {
    outstandingCents,
    overdueCents,
    dueThisMonthCents,
    paidThisMonthCents,
    outstandingCount,
    overdueCount,
  };
}

/** Cents to the studio's money, without a currency library. */
export const money = (cents: number): string =>
  (cents / 100).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  });

/**
 * What an invoice adds up to.
 *
 * Tax is a percent of the SUBTOTAL and is rounded once, at the end,
 * rather than per line: rounding each line and summing drifts by a
 * cent for every line on a long invoice, and an invoice that is a
 * few cents off its own lines is an invoice a client queries.
 *
 * The deposit is a percent of the TOTAL, tax included — it is a share
 * of the bill, not of the goods.
 */
export function invoiceMath(draft: {
  lines: { quantity: number; unitCents: number }[];
  taxRate: number;
  depositPercent: number;
}): { subtotalCents: number; taxCents: number; totalCents: number; depositCents: number } {
  const subtotalCents = draft.lines.reduce(
    (sum, l) => sum + Math.round((l.quantity || 0) * (l.unitCents || 0)),
    0,
  );
  const taxCents = Math.round((subtotalCents * (draft.taxRate || 0)) / 100);
  const totalCents = subtotalCents + taxCents;
  const depositCents = Math.round((totalCents * (draft.depositPercent || 0)) / 100);
  return { subtotalCents, taxCents, totalCents, depositCents };
}
